import { JobPhase } from '@prisma/client';
import { formatUSD } from '@utils/stringHelpers';
import { highlightStringMatches } from '@utils/stringHelpersTsx';
import { DateHelper } from '@utils/date.helper';
import InternalJobIdBadge from '~/components/pages/jobs/job-list/InternalJobIdBadge';
import PhaseIdBadge from '~/components/pages/jobs/job-list/PhaseIdBadge';
import { classNames, getFullName } from '~/components/utils';
import { FAIcon } from '~/components/utils/FAIcons';
import { useInternalJobId } from '~/hooks/useInternalJobId';
import type { JobResultOfGlobalSearch } from '~/server/schema/job.schema';

interface Props {
	job: JobResultOfGlobalSearch;
	query: string;
	isFocused: boolean;
	onClick: () => void;
}

const isWonPhase = (phase?: JobPhase) => phase === JobPhase.WORK_ORDER || phase === JobPhase.JOB_COMPLETE;

const JobSearchResultCard = ({ job, query, isFocused, onClick }: Props) => {
	const { shouldShowInternalId } = useInternalJobId();
	const isWon = isWonPhase(job.jobPhase) || (!job.jobPhase && job.totalWon > 0);
	const totalLabel = isWon ? 'Total Won' : 'Total Quoted';
	const totalAmount = isWon ? job.totalWon : job.totalQuoted;

	return (
		<button
			type="button"
			role="option"
			aria-selected={isFocused}
			onClick={onClick}
			className={classNames(
				isFocused ? 'bg-glazier-blue-50' : 'hover:bg-glazier-blue-50',
				'relative w-full px-3 py-2 text-left text-sm border-b border-gray-100 last:border-b-0'
			)}
		>
			<div className="flex items-start justify-between gap-3">
				<h3 className="font-medium text-gray-900 truncate">{highlightStringMatches(job.name, query)}</h3>
				<p className="shrink-0 text-sm text-gray-600 whitespace-nowrap">
					{totalLabel}: <span className="font-semibold text-gray-900">{formatUSD(totalAmount)}</span>
				</p>
			</div>
			<div className="mt-1 flex items-center justify-between gap-3">
				<div className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
					<span className="whitespace-nowrap">{DateHelper.formatDate({ date: job.createdAt, monthType: 'numeric' })}</span>
					<span className="text-gray-300">|</span>
					<span className="truncate">{highlightStringMatches(job.customer.name, query)}</span>
					{!!job.jobOwner && (
						<>
							<span className="text-gray-300">|</span>
							<span className="truncate">{highlightStringMatches(getFullName(job.jobOwner), query)}</span>
						</>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-2">
					{!!job.jobPhase && <PhaseIdBadge jobId={job.id} jobPhase={job.jobPhase} mtEnabled={false} />}
					{shouldShowInternalId(job.internalJobId) && <InternalJobIdBadge internalJobId={job.internalJobId!} />}
				</div>
			</div>
			{!!job.inWarrantyUntil && (
				<div className="mt-1 inline-flex items-center gap-1 rounded bg-orange-50 px-1.5 py-0.5 text-xs text-orange-900">
					<FAIcon icon="shield-check" />
					<span>Under Warranty Until {job.inWarrantyUntil}</span>
				</div>
			)}
		</button>
	);
};

export default JobSearchResultCard;
